import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

function FlashDeliveryMobile() {
  return (
    <>
      <Header />
      
      <section className="project-detail-section">
        <div className="container">
          <div className="project-detail-hero">
            <h1 className="project-detail-title">
              <span className="title-line-1">Flash Delivery –</span><br/>
              <span className="title-line-2">Mobile App & Mobile Web UX Redesign</span>
            </h1>
            <p className="project-detail-subtitle">Improving usability and structure through UX audit and redesign</p>
            <div className="project-meta">
              <span>UI/UX Designer</span>
              <span>•</span>
              <span>Figma</span>
            </div>
          </div>

          <div className="project-detail-content">
            <section className="content-section">
              <h2>Project Overview</h2>
              <p>Flash Delivery is a quick-commerce platform designed to get essentials to users in record time. However, as the service grew, the interface began to feel dated and cluttered. I was tasked with redesigning both the Mobile App and the Mobile Website to create a more seamless, intuitive experience.</p>
              
              <p>The project kicked off with a deep-dive UX audit to identify exactly where users were facing friction and how we could bridge the gap between the native app and the web browser experience.</p>
            </section>

            <section className="content-section">
              <h2>UX Audit</h2>
              <p>Before jumping into Figma, I spent time auditing the existing live products. I wanted to look at the interface through the lens of a first-time user and a power user. What I found was a series of small friction points that, when added up, made the shopping experience feel heavier than it should be.</p>
              
              <h3>Key issues discovered during the audit:</h3>
              <ul>
                <li>Weak Visual Hierarchy: It wasn't immediately clear what the primary action or focal point was on many screens.</li>
                <li>Crowded Content: Information was packed tightly, making it hard for users to scan categories quickly.</li>
                <li>Navigation Clarity: The transition between different product sections felt clunky and lacked clear signposting.</li>
                <li>Inconsistent Flows: The experience on the mobile web was significantly different from the app, leading to brand disconnection.</li>
                <li>Deal Visibility: Promotional banners were either distracting or buried, failing to drive the intended conversions.</li>
              </ul>
            </section>

            <section className="content-section">
              <h2>Old Experience (Before)</h2>
              <p>The original UI relied on a very dense grid system that left little room for 'breathing.' On the mobile web specifically, the layout didn't feel optimized for touch, with small targets and text that was difficult to read on the go.</p>
              
              <div className="mobile-images-grid">
                <img src="/assets/flash-delivery-eight.png" alt="Old Experience 1" />
                <img src="/assets/flash-delivery-ninth.png" alt="Old Experience 2" />
                <img src="/assets/flash-delivery-tenth.png" alt="Old Experience 3" />
                <img src="/assets/flash-delivery-eleventh.png" alt="Old Experience 4" />
              </div>
            </section>

            <section className="content-section">
              <h2>Redesign Strategy</h2>
              <p>My goal wasn't just to make the app 'look better,' but to make it work harder for the user. I focused on a 'Scanning-First' approach, acknowledging that quick-commerce users are usually in a rush.</p>
              
              <h3>My strategy involved:</h3>
              <ul>
                <li>Simplifying layouts to allow for faster visual processing.</li>
                <li>Creating a unified component library to align the app and web versions.</li>
                <li>Using white space strategically to reduce cognitive load.</li>
                <li>Improving the 'Search-to-Cart' flow for fewer taps and more speed.</li>
              </ul>
            </section>

            <section className="content-section">
              <h2>New Experience (Redesign)</h2>
              <p>The new designs introduce a much cleaner, more structured environment. I transitioned from tight borders to open layouts with soft shadows and rounded cards, which feel more modern and approachable. Content discovery is now driven by clear headers and optimized category cards.</p>
              
              <div className="before-redesign-images">
                <img src="/assets/flash-delivery-twelth.png" alt="New Experience 1" />
                <img src="/assets/flash-delivery-thirteenth.png" alt="New Experience 2" />
              </div>
            </section>

            <section className="content-section">
              <h2>Key UX Improvements</h2>
              <ul>
                <li>Hierarchy: Shifted from flat layouts to a clear top-down importance structure.</li>
                <li>Visibility: Promos and deals are now integrated into the flow without interrupting the user.</li>
                <li>Navigation: A more persistent and clear navigation bar that works intuitively on both iOS/Android and Web.</li>
                <li>Consistency: Shared visual language (typography, buttons, iconography) across all platforms.</li>
              </ul>
            </section>

            <section className="content-section">
              <h2>Visual Design Direction</h2>
              <p>I kept the core brand colors but updated the secondary palette to feel fresher. The typography was switched to a highly legible sans-serif font-face optimized for small screens. The use of rounded corners and card-based layouts provides a 'friendly' interface that helps compartmentalize information without the need for heavy dividers.</p>
            </section>

            <section className="content-section">
              <h2>Final Outcome</h2>
              <p>The end result is a cohesive digital ecosystem for Flash Delivery. The mobile web experience no longer feels like a secondary thought, but a fully-fledged companion to the app. Users can now navigate through categories 30% faster (based on internal walkthroughs), and the interface finally reflects the 'Flash' speed that the brand promises.</p>
            </section>

            <section className="content-section">
              <h2>Figma Link (Prototype)</h2>
              
              <div className="project-links">
                <a href="https://www.figma.com/proto/Z96dqeKjKOE0IVlMumCExE/Untitled?node-id=46-163&t=Xe3eHmjTG3OWNUoI-0&scaling=scale-down&content-scaling=fixed&page-id=0%3A1&starting-point-node-id=1%3A2" target="_blank" rel="noopener noreferrer" className="btn">
                  Mobile Web Prototype <span className="btn-arrow">→</span>
                </a>
                <a href="https://www.figma.com/proto/HAf5J2UeK8csfkry3uZ1dl/Untitled?node-id=23-6&t=qnQ0Ha4MKL758jsD-0&scaling=scale-down&content-scaling=fixed&page-id=0%3A1&starting-point-node-id=23%3A6" target="_blank" rel="noopener noreferrer" className="btn">
                  Mobile App Prototype <span className="btn-arrow">→</span>
                </a>
              </div>
            </section>

            <section className="content-section">
              <h2>Audits</h2>
              
              <div className="before-redesign-images">
                <img src="/assets/flash-delivery-fourteenth.png" alt="Audit 1" />
                <img src="/assets/flash-delivery-fifteenth.png" alt="Audit 2" />
              </div>
            </section>

            <section className="content-section">
              <h2>Learnings & Reflection</h2>
              <p>This project was a great reminder that a UI redesign should always be rooted in a thorough UX audit. It's easy to want to change everything, but the audit helped me focus on the changes that actually mattered for the user's speed and clarity.</p>
              
              <p>Ensuring a consistent experience between a native app and a mobile browser was a challenge that required careful component planning, but it ultimately led to a much stronger brand presence.</p>
            </section>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default FlashDeliveryMobile;
